import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Number Crunch — vue React staircase + debrief.
 *
 * Stage 280x280 noir, equation typographique geante. Sous-stage : keypad 3x4
 * (chiffres 0-9 + Effacer + auto-validate) ou choix Vrai/Faux pour estimate.
 * Une barre timer en haut compte le deadline restant.
 */
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DebriefShell, useGameShellFromContext } from '@labrain/game-shell';
import { staircaseConfig, variantForLevel } from './params';
const STAGE_SIZE = 280;
const OP_GLYPH = {
    add: '+',
    sub: '-',
    mul: '×',
    div: '÷',
    estimate: '≈',
};
function equationText(stim, buffer) {
    if (stim.op === 'estimate') {
        return `${stim.a} ${OP_GLYPH[stim.op]} ${stim.b} ≈ ${stim.estimatedValue ?? '?'} ?`;
    }
    const tail = buffer.length === 0 ? '?' : buffer;
    return `${stim.a} ${OP_GLYPH[stim.op]} ${stim.b} = ${tail}`;
}
function Stage({ stim, buffer, showFixation, showQuestionOnly, feedbackCorrect }) {
    const { t } = useTranslation('games');
    return (_jsxs("div", { className: "flex items-center justify-center border border-cyan-border-faint bg-cyan-surface", style: {
            width: STAGE_SIZE,
            height: STAGE_SIZE,
        }, children: [showFixation && (_jsx("span", { className: "font-display text-[48px] text-cyan-text", children: "+" })), !showFixation && stim && feedbackCorrect === null && (_jsx("span", { className: "font-display text-cyan-text text-center px-s4", style: { fontSize: 36, lineHeight: 1.2 }, children: showQuestionOnly
                    ? equationText(stim, '').replace(/= \?$/, '= ?')
                    : equationText(stim, buffer) })), feedbackCorrect !== null && stim && (_jsxs("div", { className: "flex flex-col items-center gap-s2 px-s4 text-center", children: [_jsx("span", { className: [
                            'font-display text-[44px] font-medium',
                            feedbackCorrect === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                        ].join(' '), children: feedbackCorrect === 'correct' ? '✓' : '✗' }), feedbackCorrect === 'incorrect' && (_jsx("span", { className: "text-[12px] text-cyan-text", children: stim.op === 'estimate'
                            ? t('number_crunch.feedback.timeout')
                            : t('number_crunch.feedback.incorrect_with_truth', { truth: stim.answer }) }))] }))] }));
}
function DigitKeypad({ onDigit, onClear, disabled }) {
    const { t } = useTranslation('games');
    // Layout 3x4 : 1 2 3 / 4 5 6 / 7 8 9 / Clear 0 _
    const rows = [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
        ['clear', 0, null],
    ];
    return (_jsx("div", { className: "grid gap-s2", style: { gridTemplateColumns: 'repeat(3, 64px)' }, children: rows.flat().map((cell, i) => {
            if (cell === null)
                return _jsx("div", {}, `spacer-${i}`);
            if (cell === 'clear') {
                return (_jsx("button", { type: "button", onClick: () => !disabled && onClear(), disabled: disabled, "aria-label": t('number_crunch.buttons.clear'), className: [
                        'flex h-[64px] w-[64px] items-center justify-center font-mono text-[12px] uppercase tracking-[0.20em]',
                        'transition-all duration-feedback ease-soft-wind',
                        disabled
                            ? 'cursor-not-allowed border border-cyan-border-faint bg-cyan-bg text-cyan-text-faint opacity-50'
                            : 'cursor-pointer border border-cyan-border bg-cyan-surface text-cyan-text hover:bg-cyan-bg active:scale-95',
                    ].join(' '), children: t('number_crunch.buttons.clear') }, "clear"));
            }
            return (_jsx("button", { type: "button", onClick: () => !disabled && onDigit(cell), disabled: disabled, className: [
                    'flex h-[64px] w-[64px] items-center justify-center font-display text-[26px]',
                    'transition-all duration-feedback ease-soft-wind',
                    disabled
                        ? 'cursor-not-allowed bg-cyan-bg text-cyan-text-faint opacity-50'
                        : 'cursor-pointer bg-cyan-core text-cyan-surface hover:bg-cyan-network active:scale-95',
                ].join(' '), children: cell }, `digit-${cell}`));
        }) }));
}
function EstimatePicker({ onPick, disabled }) {
    const { t } = useTranslation('games');
    return (_jsxs("div", { className: "flex items-center gap-s4", children: [_jsx("button", { type: "button", onClick: () => !disabled && onPick('true'), disabled: disabled, className: [
                    'inline-flex h-12 items-center justify-center px-s6 font-display text-[18px] font-medium tracking-[-0.01em]',
                    'transition active:scale-[0.985]',
                    disabled
                        ? 'cursor-not-allowed bg-cyan-bg text-cyan-text-faint opacity-50'
                        : 'cursor-pointer bg-cyan-core text-cyan-surface hover:bg-cyan-network',
                ].join(' '), children: t('number_crunch.buttons.true') }), _jsx("button", { type: "button", onClick: () => !disabled && onPick('false'), disabled: disabled, className: [
                    'inline-flex h-12 items-center justify-center px-s6 font-display text-[18px] font-medium tracking-[-0.01em]',
                    'transition active:scale-[0.985]',
                    disabled
                        ? 'cursor-not-allowed bg-cyan-bg text-cyan-text-faint opacity-50'
                        : 'cursor-pointer bg-cyan-media text-cyan-surface hover:bg-cyan-media/80',
                ].join(' '), children: t('number_crunch.buttons.false') })] }));
}
function TimerBar({ durationMs, active }) {
    const [progress, setProgress] = useState(1);
    const startRef = useRef(null);
    const rafRef = useRef(null);
    useEffect(() => {
        if (!active) {
            setProgress(1);
            startRef.current = null;
            if (rafRef.current !== null)
                cancelAnimationFrame(rafRef.current);
            return;
        }
        startRef.current = performance.now();
        const tick = () => {
            if (startRef.current === null)
                return;
            const elapsed = performance.now() - startRef.current;
            const ratio = Math.max(0, 1 - elapsed / durationMs);
            setProgress(ratio);
            if (ratio > 0) {
                rafRef.current = requestAnimationFrame(tick);
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current !== null)
                cancelAnimationFrame(rafRef.current);
        };
    }, [active, durationMs]);
    return (_jsx("div", { className: "h-[4px] w-full overflow-hidden rounded-full bg-cyan-border-faint", children: _jsx("div", { className: "h-full bg-cyan-core transition-none", style: { width: `${progress * 100}%` }, "aria-hidden": true }) }));
}
export function NCStaircaseView({ adapter, level: _level, onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const subscribe = useCallback((notify) => adapter.subscribe(() => notify()), [adapter]);
    const getSnapshot = useCallback(() => adapter.getState(), [adapter]);
    const adapterState = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
    const shell = useGameShellFromContext();
    const handleDigit = useCallback((d) => adapter.handleDigit(d), [adapter]);
    const handleClear = useCallback(() => adapter.handleClear(), [adapter]);
    const handleEstimate = useCallback((p) => adapter.handleEstimate(p), [adapter]);
    const trialDisplay = shell.snapshot.staircase.trialIdx + 1;
    const stim = adapterState.stim;
    const isEstimate = stim?.op === 'estimate';
    const awaiting = adapterState.subPhase === 'awaiting_input';
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s5 py-s4", children: [_jsxs("div", { className: "flex w-full items-center justify-between text-[12px] text-cyan-text-secondary", children: [_jsx("span", { className: "font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('common:labels.trial_count', {
                            current: Math.min(trialDisplay, staircaseConfig.maxTrials),
                            total: staircaseConfig.maxTrials,
                        }) }), _jsx("button", { type: "button", onClick: () => {
                            shell.abort('user');
                            onAbort();
                        }, className: "border border-cyan-border-faint px-s3 py-s1 text-[11px] text-cyan-text-secondary hover:border-cyan-border", children: t('common:actions.stop') })] }), _jsx("div", { className: "w-full max-w-[280px]", children: _jsx(TimerBar, { durationMs: stim?.deadlineMs ?? 5000, active: awaiting }) }), _jsx(Stage, { stim: stim, buffer: adapterState.inputBuffer, showFixation: adapterState.subPhase === 'fixation', showQuestionOnly: adapterState.subPhase === 'equation', feedbackCorrect: adapterState.subPhase === 'feedback' ? adapterState.feedback : null }), _jsxs("div", { className: "flex h-[24px] items-center justify-center text-[12px] text-cyan-text-secondary", children: [awaiting && !isEstimate && (_jsx("span", { children: t('number_crunch.question') })), awaiting && isEstimate && (_jsx("span", { children: t('number_crunch.question') }))] }), isEstimate ? (_jsx(EstimatePicker, { onPick: handleEstimate, disabled: !awaiting })) : (_jsx(DigitKeypad, { onDigit: handleDigit, onClear: handleClear, disabled: !awaiting }))] }));
}
function recommendKey(threshold) {
    if (threshold !== null && threshold <= 2500) {
        return 'number_crunch.debrief.recommend_excellent';
    }
    if (threshold !== null && threshold <= 4000) {
        return 'number_crunch.debrief.recommend_good';
    }
    return 'number_crunch.debrief.recommend_keep';
}
export function NCDebriefView({ level: _level, onAbort: _onAbort }) {
    const { t } = useTranslation(['games', 'common']);
    const shell = useGameShellFromContext();
    const staircase = shell.snapshot.staircase;
    const threshold = staircase.thresholdEstimate;
    const trialsCompleted = staircase.trialIdx;
    const trialsCorrect = staircase.trialLog.filter((tt) => tt.response.correct).length;
    const accuracy = trialsCompleted === 0
        ? 0
        : Math.round((trialsCorrect / trialsCompleted) * 100);
    const fastest = useMemo(() => {
        if (staircase.trialLog.length === 0)
            return null;
        const correctRTs = staircase.trialLog
            .filter((tt) => tt.response.correct)
            .map((tt) => tt.response.reactionTimeMs);
        if (correctRTs.length === 0)
            return null;
        return Math.min(...correctRTs);
    }, [staircase]);
    const fallback = !!staircase.fallback;
    const thresholdLabel = threshold === null ? '—' : `${Math.round(threshold)} ms`;
    const replay = () => {
        window.location.reload();
    };
    return (_jsx(DebriefShell, { eyebrow: t('common:eyebrow.session_done'), title: t('number_crunch.debrief.title'), scoreLabel: t('number_crunch.debrief.score_label'), scoreValue: thresholdLabel, fallbackBadge: fallback, metrics: [
            { label: t('common:metrics.trials'), value: trialsCompleted },
            { label: t('common:metrics.correct_pct'), value: `${accuracy}%` },
            {
                label: t('common:metrics.shortest'),
                value: fastest === null ? '—' : `${Math.round(fastest)} ms`,
            },
        ], recommendation: t(recommendKey(threshold)), scientificCaveat: t('number_crunch.debrief.scientific_caveat'), ctaReplay: _jsx("button", { type: "button", onClick: replay, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.replay') }), ctaBack: _jsx(Link, { to: "/library", className: "inline-flex h-12 w-full items-center justify-center border border-cyan-border bg-cyan-surface font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-text transition active:scale-[0.985] hover:bg-cyan-bg", children: t('common:actions.choose_another') }) }));
}
// Anti-warning : variantForLevel non utilisee directement ici, mais conservee
// pour cohesion API (cf. autres jeux).
void variantForLevel;
//# sourceMappingURL=staircaseView.js.map